import React, {useContext, useEffect, useMemo, useState} from "react";
import Navbar from "../../../../../common/layout/Navbar";
import {
  GridAlignment,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import {LicenseTypes, Operation} from "../../../../../../utils/Enum.types";
import {Title} from "../../../../../../stories/Title/Title";
import {Button} from "../../../../../../stories/Button/Button";
import Input from "../../../../../../stories/Input/Input";
import {useNavigate} from "react-router-dom";
import {TableHeaderProps} from "../../../../../../utils/types";
import useAssetTableJson from "../../../../../../json/useAssetTableJson";
import Edit from "../../../../../../images/EditProfile.svg";
import Delete from "../../../../../../images/Delete.svg";
import {TABLE_ROW_HEIGHT} from "../../../../../../styles/Variables";
import {ColumnVisibilityFor} from "../../../../../../utils/Enum.Datagrid";
import {StyledDatagrid} from "../../../../../../styles/DatagridStyles";
import {CustomModal} from "../../../../../../utils/UtilFunctions";
import {ViewModalStyles} from "../../../../../../styles/CustomModalStyles";
import Add from "./Add";
import {useHotKey} from "../../../../../../utils/hotKeys";
import {useLazyQuery} from "@apollo/client";
import {GetItemGroups} from "../queries/queries";
import useUrlParams from "../../../../../../customHooks/useUrlParams";
import {
  AtsItemGroupEdge,
  AtsItemGroupOrderField,
  GetItemGroupsResult,
  GetItemGroupsVars,
} from "../queries/types";
import {
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../../utils/constants";
import {OrderDirection} from "../../../../../../utils/commonQueryTypes";
import {AppContext} from "../../../../../../context/context";
import {payloadTypes} from "../../../../../../context/reducer";

export const enum GroupingListPageFor {
  GENERAL = "GENERAL",
  ASSIGN = "ASSIGN",
}
interface props {
  pageType: GroupingListPageFor;
}

export interface AssignAssetProps {
  flag: boolean;
  operation: Operation;
}

const GroupingAsset = ({pageType}: props) => {
  const navigate = useNavigate();
  const {Masters_Table} = useAssetTableJson();
  const [assignAsset, setAssignAsset] = useState<AssignAssetProps>({
    flag: false,
    operation: Operation.NONE,
  });
  const [searchData, setSearchData] = useState("");
  const {customer_id, group_id} = useUrlParams();

  const {dispatch} = useContext(AppContext);

  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);

  const [itemGroups, setItemGroups] = useState<AtsItemGroupEdge[]>([]);
  const [GetAssocs, {data, loading, fetchMore}] = useLazyQuery<
    GetItemGroupsResult,
    GetItemGroupsVars
  >(GetItemGroups);

  useEffect(() => {
    if (customer_id) {
      GetAssocs({
        variables: {
          after: null,
          customerID: customer_id,
          first: ROWS_PER_PAGE,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsItemGroupOrderField.GROUP_NAME,
          },
          name: searchData,
        },
      });
    }
  }, [customer_id, GetAssocs, searchData]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItemGroups.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = itemGroups.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItemGroups(updatedNewData);
      } else {
        setItemGroups(newData);
      }

      setEndCursor(data.GetItemGroups.pageInfo.endCursor);
      setHasNextPage(data.GetItemGroups.pageInfo.hasNextPage);
    } // eslint-disable-next-line
  }, [data, loading]);

  const handleScroll = () => {
    if (hasNextPage && !loading) {
      fetchMore({
        variables: {
          first: FETCH_MORE_DATA,
          after: endCursor,
        },
        updateQuery: (prevResult, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prevResult;

          const newEdges = fetchMoreResult.GetItemGroups.edges;
          const pageInfo = fetchMoreResult.GetItemGroups.pageInfo;
          setEndCursor(pageInfo.endCursor);
          setHasNextPage(pageInfo.hasNextPage);

          const duplicateCheck = prevResult.GetItemGroups.edges.filter(
            ({node: {id}}) =>
              newEdges.findIndex(({node: {id: newId}}) => newId === id) !== -1
          );

          if (duplicateCheck.length > 0) return prevResult;

          return {
            GetItemGroups: {
              edges: [...itemGroups, ...newEdges],
              pageInfo,
              totalCount: data ? data.GetItemGroups.totalCount! : 0,
            },
          };
        },
      });
    }
  };

  const dynamicHeaders: TableHeaderProps[] =
    Masters_Table.Masters.Grouping.Asset.map((header) => ({
      headerName: header.headerName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      cellClassName: header.cellClassName,
      renderCell: (params) => {
        if (header.field === "actions") {
          return (
            <>
              <img
                src={Edit}
                alt=""
                onClick={() => {
                  dispatch({
                    type: payloadTypes.SET_GROUP_ID,
                    payload: {
                      groupId: params.row.grpId,
                    },
                  });
                  setAssignAsset({
                    flag: true,
                    operation: Operation.UPDATE,
                  });
                }}
              />
              {/* <img src={Delete} alt="" /> */}
            </>
          );
        } else if (header.field === "asset_list") {
          return (
            <>
              <ul className="grouping-asset__ul">
                {params.value.map((itm: string, index: number) => {
                  return (
                    <li>
                      {index + 1}. {itm}
                    </li>
                  );
                })}
              </ul>
            </>
          );
        }

        return params.value;
      },
    }));
  const columns: GridColDef[] = [...dynamicHeaders];

  const rows: GridValidRowModel[] = useMemo(() => {
    return itemGroups.map((itm, index) => ({
      id: index + 1,
      grouping_name: itm.node.groupName,
      asset_list: itm.node.groupAssoicDetails.map(
        ({itemDetails}) => itemDetails.desc
      ),
      total_assets: itm.node.totalQty,
      grpId: itm.node.id,
    }));
  }, [itemGroups]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    const savedModel = localStorage.getItem(ColumnVisibilityFor.GROUPING_ASSET);
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.GROUPING_ASSET,
      JSON.stringify(newModel)
    );
  };

  useHotKey({
    charater: "a",
    handleFunc: () => {
      setAssignAsset({
        flag: true,
        operation: Operation.CREATE,
      });
    },
    specialCharacter: "ctrlKey",
  });
  return (
    <>
      <Navbar NavType={LicenseTypes.COMPANY} />
      <Title>Grouping Asset</Title>
      <div className="grouping-asset">
        <div className="grouping-asset__search row g-0">
          <div className="col-2">
            <Input id="data_search" placeholder="Search..." onChange={(e) => setSearchData(e.target.value)} />
          </div>
          <div className="col"></div>
          <div className="col-2 flex-end">
            <Button
              mode="addnew"
              onClick={() => {
                if (pageType === GroupingListPageFor.ASSIGN) {
                  setAssignAsset({
                    flag: true,
                    operation: Operation.UPDATE,
                  });
                } else
                  setAssignAsset({
                    flag: true,
                    operation: Operation.CREATE,
                  });
              }}
            />
          </div>
        </div>
        <div className="grouping-asset__tableblock">
          <StyledDatagrid
            columns={columns}
            rows={rows}
            // rowHeight={TABLE_ROW_HEIGHT}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            initialState={{
              columns: {
                columnVisibilityModel: columnVisibilityModel,
              },
            }}
            disableRowSelectionOnClick
            hideFooter
            onRowsScrollEnd={handleScroll}
            getRowHeight={() => "auto"}
          />
        </div>
        <Button mode="back" onClick={() => navigate(-1)} />
      </div>

      <CustomModal
        style={ViewModalStyles}
        isOpen={assignAsset.flag}
        onRequestClose={() =>
          setAssignAsset({
            flag: false,
            operation: Operation.NONE,
          })
        }
        contentComponent={
          <Add setModal={setAssignAsset} operation={assignAsset.operation} />
        }
      />
    </>
  );
};

export default GroupingAsset;
